import { useId, useMemo } from 'react';
import { getStroke, type StrokeOptions } from 'perfect-freehand';
import type { StrokeItem } from '../../doc/schema';
import type { InkPreview } from '../paneStore';

/** Highlighter ink is see-through by nature; a stroke's own opacity applies on top. */
export const HIGHLIGHTER_ALPHA = 0.45;

/**
 * Freehand ink, scaled with the item box: lines are a perfect-freehand outline, fills the drawn
 * loop itself, with the eraser's paths masked out. While the eraser works on a stroke, `preview`
 * stands in for what it has left of it.
 */
export function StrokeBody({ item, preview }: { item: StrokeItem; preview?: InkPreview }) {
  const { points, w0, h0, color, tool, size, pressure, cut, opacity } = item.props;
  const erased = preview?.erased ?? item.props.erased;
  const runs = preview?.runs;
  // pieces are separate paths, as they will be separate strokes: one path would cancel out where they cross
  const paths = useMemo(
    () => (runs ? runs.map((r) => strokePath(r.points, tool, size, pressure, r.cut)) : [strokePath(points, tool, size, pressure, cut)]),
    [runs, points, tool, size, pressure, cut],
  );
  // ids must be unique in the document: the same item can be on screen in both split panes
  const maskId = `erase-${item.id}-${useId().replace(/[^\w-]/g, '')}`;
  const masked = tool === 'fill' && !!erased?.length;
  const vw = Math.max(1, w0);
  const vh = Math.max(1, h0);
  return (
    <svg className={`stroke-svg stroke-${tool}`} viewBox={`0 0 ${vw} ${vh}`} preserveAspectRatio="none" overflow="visible" aria-hidden>
      {masked && (
        <mask id={maskId}>
          <rect x={-vw} y={-vh} width={vw * 3} height={vh * 3} fill="#fff" />
          {erased.map((path, i) => (
            <path
              key={i}
              d={polylinePath(path)}
              fill="none"
              stroke="#000"
              strokeWidth={(path[0] ?? 0) * 2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </mask>
      )}
      {paths.map((d, i) => (
        <path key={i} d={d} fill={color} fillOpacity={opacity ?? 1} mask={masked ? `url(#${maskId})` : undefined} />
      ))}
    </svg>
  );
}

/**
 * How each tool turns points into an outline. `size` is the width at half pressure. Tool names
 * this client does not know draw like the pen.
 */
export function strokeOptions(tool: string, size: number, realPressure: boolean, cut = 0): StrokeOptions {
  switch (tool) {
    case 'fineliner':
    case 'highlighter':
      // a constant tip: pressure and speed never change the width
      return { size, thinning: 0, smoothing: 0.5, streamline: 0.45, simulatePressure: false, last: true };
    case 'brush': {
      // strong pressure response and pointed ends, except where the eraser cut the stroke
      const taper = size * 3;
      return {
        size,
        thinning: 0.75,
        smoothing: 0.6,
        streamline: 0.5,
        simulatePressure: !realPressure,
        start: { taper: cut & 1 ? 0 : taper },
        end: { taper: cut & 2 ? 0 : taper },
        last: true,
      };
    }
    default:
      return { size, thinning: 0.55, smoothing: 0.5, streamline: 0.45, simulatePressure: !realPressure, last: true };
  }
}

/** Flat [x, y, p, ...] to an SVG path: the outline of a line, or the area of a fill. */
export function strokePath(flat: number[], tool: string, size: number, realPressure: boolean, cut = 0): string {
  const pts: [number, number, number][] = [];
  for (let i = 0; i + 2 < flat.length; i += 3) pts.push([flat[i]!, flat[i + 1]!, flat[i + 2]!]);
  if (!pts.length) return '';
  if (tool === 'fill') return pts.length < 3 ? '' : outlineToPath(pts);
  return outlineToPath(getStroke(pts, strokeOptions(tool, size, realPressure, cut)));
}

/** A closed, smoothed path through the points (quadratic curves between their midpoints). */
export function outlineToPath(outline: number[][]): string {
  if (outline.length < 2) return '';
  let d = '';
  const n = outline.length;
  for (let i = 0; i < n; i++) {
    const a = outline[i]!;
    const b = outline[(i + 1) % n]!;
    const mx = (a[0]! + b[0]!) / 2;
    const my = (a[1]! + b[1]!) / 2;
    d += i === 0 ? `M${a[0]!.toFixed(2)},${a[1]!.toFixed(2)}` : '';
    d += `Q${a[0]!.toFixed(2)},${a[1]!.toFixed(2)} ${mx.toFixed(2)},${my.toFixed(2)}`;
  }
  return d + 'Z';
}

/** An eraser path [radius, x, y, x, y, ...] as an open polyline. */
function polylinePath(path: number[]): string {
  if (path.length < 3) return '';
  let d = `M${path[1]!.toFixed(2)},${path[2]!.toFixed(2)}`;
  // a lone point: a sliver of a segment, so it gets its round caps
  if (path.length < 5) return d + 'h0.01';
  for (let i = 3; i + 1 < path.length; i += 2) d += `L${path[i]!.toFixed(2)},${path[i + 1]!.toFixed(2)}`;
  return d;
}

const MAX_MASK_SIDE = 4096;

/**
 * Paints a stroke onto a 2D canvas whose transform maps the item's box to (0, 0)-(w, h).
 * `blend` is the highlighter's blend mode (the board uses multiply on light canvases).
 */
export function paintStroke(ctx: CanvasRenderingContext2D, item: StrokeItem, blend?: GlobalCompositeOperation): void {
  const { points, w0, h0, color, tool, size, pressure, cut, opacity = 1, erased } = item.props;
  const path = new Path2D(strokePath(points, tool, size, pressure, cut));
  const sx = item.w / Math.max(1, w0);
  const sy = item.h / Math.max(1, h0);
  ctx.save();
  ctx.globalAlpha *= (tool === 'highlighter' ? HIGHLIGHTER_ALPHA : 1) * opacity;
  if (tool === 'highlighter' && blend) ctx.globalCompositeOperation = blend;
  if (tool !== 'fill' || !erased?.length) {
    ctx.scale(sx, sy);
    ctx.fillStyle = color;
    ctx.fill(path);
    ctx.restore();
    return;
  }
  // A fill with erased paths: paint it on a scratch canvas covering only the part of the
  // target it lands on, rub the paths out there, then copy the result across.
  const m = ctx.getTransform();
  const corners = [
    m.transformPoint({ x: 0, y: 0 }),
    m.transformPoint({ x: item.w, y: 0 }),
    m.transformPoint({ x: 0, y: item.h }),
    m.transformPoint({ x: item.w, y: item.h }),
  ];
  const x0 = Math.max(0, Math.floor(Math.min(...corners.map((c) => c.x))));
  const y0 = Math.max(0, Math.floor(Math.min(...corners.map((c) => c.y))));
  const x1 = Math.min(ctx.canvas.width, Math.ceil(Math.max(...corners.map((c) => c.x))));
  const y1 = Math.min(ctx.canvas.height, Math.ceil(Math.max(...corners.map((c) => c.y))));
  if (x1 <= x0 || y1 <= y0) {
    ctx.restore();
    return;
  }
  // past the size limit the scratch canvas is coarser than the target: scale it back up
  const k = Math.min(1, MAX_MASK_SIDE / (x1 - x0), MAX_MASK_SIDE / (y1 - y0));
  const scratch = document.createElement('canvas');
  scratch.width = Math.max(1, Math.round((x1 - x0) * k));
  scratch.height = Math.max(1, Math.round((y1 - y0) * k));
  const o = scratch.getContext('2d');
  if (!o) {
    ctx.restore();
    return;
  }
  o.setTransform(k, 0, 0, k, -x0 * k, -y0 * k);
  o.transform(m.a, m.b, m.c, m.d, m.e, m.f);
  o.scale(sx, sy);
  o.fillStyle = color;
  o.fill(path);
  o.globalCompositeOperation = 'destination-out';
  o.lineCap = 'round';
  o.lineJoin = 'round';
  o.strokeStyle = '#000';
  for (const p of erased) {
    if (p.length < 3) continue;
    o.lineWidth = p[0]! * 2;
    o.beginPath();
    o.moveTo(p[1]!, p[2]!);
    // canvas drops zero-length segments: a lone point gets a sliver, so it has round caps
    if (p.length < 5) o.lineTo(p[1]! + 0.01, p[2]!);
    for (let i = 3; i + 1 < p.length; i += 2) o.lineTo(p[i]!, p[i + 1]!);
    o.stroke();
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.drawImage(scratch, x0, y0, x1 - x0, y1 - y0);
  ctx.restore();
}
